import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Paper, Chip, 
  Table, TableHead, TableRow, TableCell, TableBody, Button, Divider, IconButton, Tooltip, 
} from "@mui/material";
import {
  Hotel, CalendarMonth, AttachMoney, People, TrendingUp, Star, 
  Home, Today, CheckCircle, Pending, Cancel, Refresh, Business,
  MeetingRoom, LocationCity,
} from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const API = api_urls + "api/sanatani-hotel";

const HotelAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + "/admin/analytics");
      if (res.data?.success) setD(res.data.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress size={50} /></Box>;
  if (!d) return <Box p={3}><Typography>Failed to load analytics</Typography></Box>;

  const mainStats = [
    { title: "Total Partners", value: d.totalPartners, icon: <People fontSize="large" />, color: "#1565C0", bg: "#E3F2FD", sub: d.approvedPartners + " approved, " + d.pendingPartners + " pending" },
    { title: "Total Properties", value: d.totalProperties, icon: <Hotel fontSize="large" />, color: "#2E7D32", bg: "#E8F5E9", sub: d.approvedProperties + " approved, " + d.pendingProperties + " pending" },
    { title: "Total Rooms", value: d.totalRooms, icon: <MeetingRoom fontSize="large" />, color: "#00838F", bg: "#E0F7FA", sub: "Across all properties" },
    { title: "Total Bookings", value: d.totalBookings, icon: <CalendarMonth fontSize="large" />, color: "#E65100", bg: "#FFF3E0", sub: d.activeBookings + " active now" },
    { title: "Today's Bookings", value: d.todayBookings, icon: <Today fontSize="large" />, color: "#AD1457", bg: "#FCE4EC", sub: "This week: " + d.weekBookings },
    { title: "Month Revenue", value: "₹" + (d.totalRevenue || 0).toLocaleString(), icon: <AttachMoney fontSize="large" />, color: "#6A1B9A", bg: "#EDE7F6", sub: d.monthBookingsCount + " bookings this month" },
    { title: "Commission Earned", value: "₹" + (d.totalCommission || 0).toLocaleString(), icon: <TrendingUp fontSize="large" />, color: "#00695C", bg: "#E0F2F1", sub: "From all bookings" },
    { title: "Pending Approvals", value: d.pendingPartners + d.pendingProperties, icon: <Pending fontSize="large" />, color: "#F57F17", bg: "#FFF8E1", sub: d.pendingPartners + " partners, " + d.pendingProperties + " properties" },
  ];

  const statusColors = { confirmed: "#4CAF50", pending: "#FF9800", cancelled: "#F44336", checked_in: "#2196F3", checked_out: "#9E9E9E", no_show: "#795548" };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800}>🛕 Sanatani Sadan Dashboard</Typography>
          <Typography variant="body2" color="textSecondary">Real-time analytics & management overview</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
      </Box>

      {/* Main Stats */}
      <Grid container spacing={2.5}>
        {mainStats.map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderLeft: "4px solid " + s.color, height: "100%", transition: "transform 0.2s", "&:hover": { transform: "translateY(-3px)", boxShadow: 4 } }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ backgroundColor: s.bg, borderRadius: "50%", width: 56, height: 56, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, flexShrink: 0 }}>
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography color="textSecondary" variant="body2" fontSize={12}>{s.title}</Typography>
                    <Typography variant="h4" fontWeight={700}>{s.value}</Typography>
                    <Typography variant="caption" color="textSecondary">{s.sub}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Booking Status + Top Cities Row */}
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>📊 Booking Status Breakdown</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {Object.entries(d.statusBreakdown || {}).map(([status, count]) => (
                <Grid item xs={6} sm={4} key={status}>
                  <Card variant="outlined" sx={{ borderColor: statusColors[status] || "#ccc" }}>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Typography variant="h4" fontWeight={700} color={statusColors[status] || "primary"}>{count}</Typography>
                      <Typography variant="body2" color="textSecondary" textTransform="capitalize" mt={1}>{status.replace(/_/g, " ")}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {Object.keys(d.statusBreakdown || {}).length === 0 && (
                <Grid item xs={12}><Typography color="textSecondary" textAlign="center">No bookings yet</Typography></Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>🏙️ Top Cities</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={1.5}>
              {(d.topCities || []).map((c, i) => (
                <Grid item xs={6} sm={4} key={i}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                      <LocationCity sx={{ color: "#1565C0", mb: 0.5 }} />
                      <Typography fontWeight={600} fontSize={14}>{c._id || "Unknown"}</Typography>
                      <Typography variant="caption" color="textSecondary">{c.count} properties</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Property Types */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>🏨 Property Types</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" gap={2} flexWrap="wrap">
          {(d.propertyTypes || []).map((pt, i) => (
            <Chip key={i} label={pt._id + " (" + pt.count + ")"} variant="outlined" 
              sx={{ fontSize: 14, py: 2.5, fontWeight: 600, textTransform: "capitalize" }} />
          ))}
        </Box>
      </Paper>

      {/* Recent Bookings Table */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>📋 Recent Bookings</Typography>
        <Divider sx={{ mb: 2 }} />
        {(d.recentBookings || []).length === 0 ? (
          <Typography color="textSecondary" textAlign="center" py={3}>No bookings yet</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><b>Guest</b></TableCell>
                <TableCell><b>Property</b></TableCell>
                <TableCell><b>City</b></TableCell>
                <TableCell><b>Check In</b></TableCell>
                <TableCell><b>Check Out</b></TableCell>
                <TableCell><b>Amount</b></TableCell>
                <TableCell><b>Status</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.recentBookings.map((b, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={13}>{b.guestName}</Typography>
                    <Typography variant="caption" color="textSecondary">{b.guestPhone}</Typography>
                  </TableCell>
                  <TableCell>{b.propertyName}</TableCell>
                  <TableCell>{b.propertyCity}</TableCell>
                  <TableCell>{b.checkIn ? new Date(b.checkIn).toLocaleDateString("en-IN") : "-"}</TableCell>
                  <TableCell>{b.checkOut ? new Date(b.checkOut).toLocaleDateString("en-IN") : "-"}</TableCell>
                  <TableCell><b>₹{b.amount}</b></TableCell>
                  <TableCell>
                    <Chip label={b.status?.replace(/_/g, " ")} size="small"
                      sx={{ backgroundColor: (statusColors[b.status] || "#ccc") + "20", color: statusColors[b.status] || "#666", fontWeight: 600, textTransform: "capitalize" }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default HotelAnalytics;
